import { useEffect, useState } from "react";
import { Loader2, CreditCard, RotateCcw, XCircle, Clock, Repeat, ArrowRight, CheckCircle2 } from "lucide-react";
import API from "@/lib/api";

const ICONS = {
  charge: { icon: CheckCircle2, color: "text-emerald-600", ring: "ring-emerald-200 bg-emerald-50" },
  refund: { icon: RotateCcw, color: "text-amber-600", ring: "ring-amber-200 bg-amber-50" },
  failed: { icon: XCircle, color: "text-red-600", ring: "ring-red-200 bg-red-50" },
  expired: { icon: Clock, color: "text-slate-500", ring: "ring-slate-200 bg-slate-50" },
  checkout_started: { icon: Clock, color: "text-slate-500", ring: "ring-slate-200 bg-slate-50" },
  subscription: { icon: Repeat, color: "text-purple-600", ring: "ring-purple-200 bg-purple-50" },
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

export default function PatientPaymentTimeline({ patientId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ events: [], totals: {} });
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr("");
      try {
        const { data } = await API.get(`/admin/patients/${encodeURIComponent(patientId)}/payment-timeline`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || "Couldn't load payment timeline");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [patientId]);

  if (loading) return <div className="flex items-center gap-2 text-sm text-slate-500 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading payment timeline…</div>;
  if (err) return <div className="text-sm text-red-600 py-4" data-testid="patient-payment-timeline-error">{err}</div>;
  if (!data.events?.length) return <div className="text-sm text-slate-500 py-6 text-center" data-testid="patient-payment-timeline-empty">No billing activity yet.</div>;

  return (
    <div data-testid="patient-payment-timeline">
      {/* Totals strip */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Paid to date</div>
          <div className="text-lg font-semibold text-emerald-900" data-testid="timeline-total-paid">${(data.totals?.paid ?? 0).toFixed(2)}</div>
        </div>
        <div className="rounded-xl bg-amber-50 border border-amber-100 p-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Refunded</div>
          <div className="text-lg font-semibold text-amber-900" data-testid="timeline-total-refunded">${(data.totals?.refunded ?? 0).toFixed(2)}</div>
        </div>
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Events</div>
          <div className="text-lg font-semibold text-slate-800" data-testid="timeline-total-events">{data.totals?.count ?? 0}</div>
        </div>
      </div>

      {/* Vertical timeline */}
      <ol className="relative border-l-2 border-slate-200 ml-3 space-y-4 pb-2">
        {data.events.map((ev, i) => {
          const meta = ICONS[ev.kind] || ICONS.subscription;
          const Icon = meta.icon;
          return (
            <li key={i} className="ml-6 relative" data-testid={`timeline-event-${ev.kind}-${i}`}>
              <span className={`absolute -left-[34px] top-0 w-7 h-7 rounded-full ring-4 ${meta.ring} flex items-center justify-center`}>
                <Icon className={`w-3.5 h-3.5 ${meta.color}`} />
              </span>
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 leading-tight">{ev.title}</p>
                    {ev.subtitle ? <p className="text-xs text-slate-500 mt-0.5">{ev.subtitle}</p> : null}
                  </div>
                  {ev.amount ? (
                    <div className={`text-sm font-semibold shrink-0 ${ev.kind === "refund" ? "text-amber-700" : "text-slate-900"}`}>
                      {ev.kind === "refund" ? "-" : ""}${Number(ev.amount).toFixed(2)}
                    </div>
                  ) : null}
                </div>
                <div className="flex items-center gap-3 text-[11px] text-slate-400 mt-1.5 flex-wrap">
                  <span>{formatDate(ev.at)}</span>
                  {ev.invoice_number ? <span className="font-mono">{ev.invoice_number}</span> : null}
                  {ev.subscription_id ? <span className="font-mono truncate max-w-[140px]">{ev.subscription_id}</span> : null}
                  {ev.status ? <span className="uppercase tracking-wider font-semibold">{ev.status}</span> : null}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
